import { useEffect, useState } from "react";
import { Search, TriangleAlert } from "lucide-react";
import { fetchFindings, type Finding } from "../lib/api";
import { clsx } from "clsx";
import {
  PageHeader,
  SeverityBadge,
  LoadingState,
  ErrorState,
  EmptyState,
} from "../components/ui";

const TABS = ["All", "Critical", "High", "Medium", "Low"];

export default function Findings() {
  const [findings, setFindings] = useState<Finding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    setError(null);
    fetchFindings()
      .then((data) => setFindings(Array.isArray(data) ? data : []))
      .catch((e) => setError(e instanceof Error ? e.message : "Unknown error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = findings.filter((f) => {
    if (activeTab !== "All" && f.severity !== activeTab.toLowerCase()) return false;
    if (search && !f.title.toLowerCase().includes(search.toLowerCase()) && !(f.endpoint || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Findings"
        description="Every vulnerability discovered across all of your scans."
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-line bg-ink-900 p-0.5">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={clsx(
                "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                activeTab === tab
                  ? "bg-ink-700 text-mist-100"
                  : "text-mist-400 hover:text-mist-200",
              )}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="relative min-w-[220px] flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mist-500" />
          <input
            className="field pl-9"
            placeholder="Search findings…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading && <LoadingState label="Loading findings…" />}
      {error && <ErrorState message={error} onRetry={load} />}

      {!loading && !error && findings.length === 0 && (
        <EmptyState
          icon={<TriangleAlert className="h-6 w-6" />}
          title="No findings"
          description="Once a scan completes, discovered vulnerabilities will show up here with severity, CVSS, and remediation detail."
        />
      )}

      {!loading && !error && findings.length > 0 && (
        <div className="table-shell">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-mist-500">
                <th className="w-28 px-4 py-3 font-medium">Severity</th>
                <th className="px-4 py-3 font-medium">Finding</th>
                <th className="px-4 py-3 font-medium">Endpoint</th>
                <th className="w-20 px-4 py-3 font-medium">CVSS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-mist-400">
                    No findings match your filters.
                  </td>
                </tr>
              ) : (
                filtered.map((f) => (
                  <tr key={f.id} className="table-row border-b border-line/60 last:border-0">
                    <td className="px-4 py-3">
                      <SeverityBadge severity={f.severity} />
                    </td>
                    <td className="px-4 py-3 text-mist-200">{f.title}</td>
                    <td className="max-w-[280px] truncate px-4 py-3 font-mono text-xs text-mist-400">
                      {f.endpoint || "—"}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-mist-400">
                      {f.cvss_score ?? "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
